import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { generateConfig } from "../../../utils/api";
import SearchDropdown from "../SearchDropdown";
import { rescheduleAppointment } from "../../../store/store";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

export default function RescheduleComponent({
  appointmentId,
  clientView,
  onReschedule,
  onClose,
}) {
  const dispatch = useDispatch();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rescheduleStatus, setRescheduleStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [newDateTime, setNewDateTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availability, setAvailability] = useState({});
  const [clinicianData, setClinicianData] = useState(null);
  const [clinicianAppointments, setClinicianAppointments] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [daysWithSlots, setDaysWithSlots] = useState(new Set());
  const { config, baseURL } = generateConfig();
  const [isEditingClinician, setIsEditingClinician] = useState(false);
  const [clinicianSearch, setClinicianSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [isEditingClinic, setIsEditingClinic] = useState(false);

  const handleClinicianSearch = async (e) => {
    setClinicianSearch(e.target.value);
    if (e.target.value.length >= 2) {
      try {
        const response = await axios.get(
          `${baseURL}/api/clinicians/search?query=${e.target.value}`,
          config
        );
        setSearchResults(response.data);
      } catch (err) {
        console.error("Error searching clinicians:", err);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleClinicianSelect = async (clinician) => {
    setSelectedClinician(clinician);
    setIsEditingClinician(false);
    setClinicianSearch("");
    setSearchResults([]);

    // Fetch new clinician's appointments and availability
    try {
      const clinicianAppointments = await axios.get(
        `${baseURL}/api/appointments/clinician/id/${clinician._id}`,
        config
      );
      setClinicianAppointments(clinicianAppointments.data);

      const clinicianResponse = await axios.get(
        `${baseURL}/api/clinicians/${clinician._id}`,
        config
      );
      const targetClinicId = selectedClinic ? selectedClinic._id : appointment.clinic._id;
      
      // Use current appointment date or current date to find appropriate availability block
      const targetDate = appointment ? new Date(appointment.start) : new Date();
      const availability = findAvailabilityForDate(
        clinicianResponse.data.availability, 
        targetClinicId, 
        targetDate
      );
      if (!availability) {
        setError("No availability found for this clinic");
        setAvailability({});
        return;
      }
      setAvailability(availability.schedule);
      setClinicianData(clinicianResponse.data);

      // Filter available clinics for the selected clinician
      const filteredClinics = filterClinicsForClinician(clinicianResponse.data, clinics);
      setAvailableClinics(filteredClinics);

      // Reset selected date and time
      setSelectedDate("");
      setNewDateTime("");
      setAvailableSlots([]);
    } catch (err) {
      setError("Unable to fetch clinician schedule");
    }
  };

  const findAvailabilityForDate = (clinicianAvailability, clinicId, targetDate) => {
    if (!clinicianAvailability || !clinicId || !targetDate) {
      return null;
    }
    
    const targetDateTime = new Date(targetDate);
    
    // Find availability blocks for the specified clinic that include the target date
    const availableBlocks = clinicianAvailability.filter(block => {
      if (block.clinic !== clinicId) return false;
      
      const startDate = new Date(block.startDate);
      const endDate = new Date(block.endDate);
      
      return targetDateTime >= startDate && targetDateTime <= endDate;
    });
    
    // If multiple blocks exist for the same date, merge their schedules
    if (availableBlocks.length === 0) {
      return null;
    }
    
    // For now, return the first matching block
    // TODO: In the future, we might want to merge multiple blocks for the same date
    return availableBlocks[0];
  };

  const filterClinicsForClinician = (clinicianData, allClinics, targetDate = new Date()) => {
    if (!clinicianData.availability || !allClinics) {
      return [];
    }
    
    // Get unique clinic IDs that have availability for the target date
    const availableClinicIds = new Set();
    
    clinicianData.availability.forEach(block => {
      const startDate = new Date(block.startDate);
      const endDate = new Date(block.endDate);
      
      if (targetDate >= startDate && targetDate <= endDate) {
        availableClinicIds.add(block.clinic);
      }
    });
    
    const filteredClinics = allClinics.filter(clinic => 
      availableClinicIds.has(clinic._id)
    );
    
    return filteredClinics;
  };

  const handleClinicSelect = async (clinic) => {
    setSelectedClinic(clinic);
    setIsEditingClinic(false);
    
    const targetClinicianId = selectedClinician ? selectedClinician._id : appointment.clinician._id;
    
    try {
      // Fetch clinician appointments for the new clinic
      const clinicianAppointments = await axios.get(
        `${baseURL}/api/appointments/clinician/id/${targetClinicianId}`,
        config
      );
      setClinicianAppointments(clinicianAppointments.data);
      
      // Fetch clinician availability for the new clinic
      const clinicianResponse = await axios.get(
        `${baseURL}/api/clinicians/${targetClinicianId}`,
        config
      );
      
      // Use current appointment date or current date to find appropriate availability block  
      const targetDate = appointment ? new Date(appointment.start) : new Date();
      const availability = findAvailabilityForDate(
        clinicianResponse.data.availability,
        clinic._id,
        targetDate
      );
      
      if (!availability || !availability.schedule) {
        setError(`${selectedClinician ? 'Selected' : 'Current'} clinician has no availability for ${clinic.name}`);
        setAvailability({});
        return;
      }
      
      setAvailability(availability.schedule);
      setClinicianData(clinicianResponse.data);
      
      // Reset selected date and time since availability changed
      setSelectedDate("");
      setNewDateTime("");
      setAvailableSlots([]);
      
      // Clear any previous errors
      setError(null);
      
    } catch (err) {
      setError("Unable to fetch availability for selected clinic");
      setAvailability({});
    }
  };

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!appointmentId) {
        setError("No appointment ID provided");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${baseURL}/api/appointments/id/${appointmentId}`,
          config
        );
        if (!response.data) {
          setError("Appointment not found");
          setLoading(false);
          return;
        }
        setAppointment(response.data);
        setSelectedDate(
          new Date(response.data.start).toISOString().split("T")[0]
        );
        setLoading(false);

        // Fetch availability after appointment is loaded
        if (response.data?.clinician?._id) {
          try {
            const clinicianAppointments = await axios.get(
              `${baseURL}/api/appointments/clinician/id/${response.data.clinician._id}`,
              config
            );
            if (!clinicianAppointments.data) {
              setError("No availability data found");
              return;
            }
            setClinicianAppointments(clinicianAppointments.data);
          } catch (err) {
            setError("Unable to fetch clinician availability");
            setLoading(false);
          }
          try {
            const clinicianResponse = await axios.get(
              `${baseURL}/api/clinicians/${response.data.clinician._id}`,
              config
            );
            // Use appointment date to find appropriate availability block
            const appointmentDate = new Date(response.data.start);
            const availability = findAvailabilityForDate(
              clinicianResponse.data.availability,
              response.data.clinic._id,
              appointmentDate
            );
            if (!availability) {
              setError("No availability found for this clinic");
              setAvailability({});
              setLoading(false);
              return;
            }
            setAvailability(availability.schedule);
            setClinicianData(clinicianResponse.data);
            
            // Fetch all clinics for clinic selection dropdown
            try {
              const clinicsResponse = await axios.get(`${baseURL}/api/clinics`, config);
              setClinics(clinicsResponse.data);
              
              // Filter clinics based on the current clinician's availability
              const filteredClinics = filterClinicsForClinician(clinicianResponse.data, clinicsResponse.data);
              setAvailableClinics(filteredClinics);
              
            } catch (err) {
              // Silently handle clinic fetch errors - not critical for functionality
            }
          } catch (err) {
            setError("Unable to fetch clinician availability");
            setLoading(false);
          }
        }
      } catch (err) {
        setError("Unable to fetch appointment details");
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [appointmentId]);

  useEffect(() => {
    if (!clinicianAppointments || !appointment || !clinicianData) {
      return;
    }

    // We need to check each day individually for availability blocks
    // since different dates might have different availability schedules
    const availableDays = new Set();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const targetClinicId = selectedClinic ? selectedClinic._id : appointment.clinic._id;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" });
      
      // Find the availability block that applies to this specific date
      const availabilityForDate = findAvailabilityForDate(
        clinicianData.availability,
        targetClinicId,
        date
      );
      
      if (!availabilityForDate || !availabilityForDate.schedule) {
        continue;
      }
      
      const dailySchedule = availabilityForDate.schedule[dayOfWeek] || [];

      if (dailySchedule.length > 0) {
        const dateString = date.toISOString().split("T")[0];
        const appointmentsOnDate = clinicianAppointments.filter((apt) => {
          const aptDate = new Date(apt.start).toISOString().split("T")[0];
          return aptDate === dateString && apt._id !== appointment._id;
        });

        // Check if there are any available slots on this day
        let hasAvailableSlot = false;
        dailySchedule.forEach((schedule) => {
          if (!schedule.start || !schedule.end) return;
          
          const [startHour, startMinute] = schedule.start.split(":");
          let currentTime = new Date(date);
          currentTime.setHours(parseInt(startHour), parseInt(startMinute), 0);

          const [endHour, endMinute] = schedule.end.split(":");
          const endTime = new Date(date);
          endTime.setHours(parseInt(endHour), parseInt(endMinute), 0);

          const appointmentDuration =
            new Date(appointment.end).getTime() -
            new Date(appointment.start).getTime();

          while (currentTime < endTime) {
            const slotEndTime = new Date(
              currentTime.getTime() + appointmentDuration
            );

            const hasConflict = appointmentsOnDate.some((apt) => {
              const aptStart = new Date(apt.start);
              const aptEnd = new Date(apt.end);
              return (
                (currentTime >= aptStart && currentTime < aptEnd) ||
                (slotEndTime > aptStart && slotEndTime <= aptEnd) ||
                (currentTime <= aptStart && slotEndTime >= aptEnd)
              );
            });

            if (!hasConflict && slotEndTime <= endTime) {
              hasAvailableSlot = true;
              break;
            }
            currentTime = new Date(currentTime.getTime() + 15 * 60000); // 15 minute intervals
          }
          
          if (hasAvailableSlot) return; // Break out of forEach
        });

        if (hasAvailableSlot) {
          availableDays.add(dateString);
        }
      }
    }

    setDaysWithSlots(availableDays);
  }, [currentMonth, clinicianData, clinicianAppointments, appointment, selectedClinic]);

  useEffect(() => {
    if (
      !selectedDate ||
      !clinicianData ||
      !clinicianAppointments ||
      !appointment
    ) {
      return;
    }

    const [year, month, day] = selectedDate.split("-").map(Number);
    const selectedDateObj = new Date(year, month - 1, day);

    // Find the availability block that applies to this specific date
    const targetClinicId = selectedClinic ? selectedClinic._id : appointment.clinic._id;
    const availabilityForDate = findAvailabilityForDate(
      clinicianData.availability,
      targetClinicId,
      selectedDateObj
    );

    if (!availabilityForDate || !availabilityForDate.schedule) {
      setAvailableSlots([]);
      return;
    }

    // Map day numbers to day names
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayOfWeek = daysOfWeek[selectedDateObj.getUTCDay()]; // Use UTC to align with EST clinic hours

    const dailySchedule = (availabilityForDate.schedule[dayOfWeek] || []).filter(
      (schedule) => schedule.start && schedule.end
    );

    if (dailySchedule.length === 0) {
      setAvailableSlots([]);
      return;
    }

    // Filter appointments to the selected date
    const appointmentsOnDate = clinicianAppointments.filter((apt) => {
      const aptDateISO = new Date(apt.start).toISOString().split("T")[0];
      return aptDateISO === selectedDate && apt._id !== appointment._id;
    });

    const slots = [];
    dailySchedule.forEach((schedule) => {
      // Convert EST clinic hours to UTC
      const [startHour, startMinute] = schedule.start.split(":").map(Number);
      const [endHour, endMinute] = schedule.end.split(":").map(Number);

      let currentTime = new Date(selectedDateObj);
      currentTime.setUTCHours(startHour + 5, startMinute, 0); // Convert EST to UTC (+5 hours for standard time)

      const endTime = new Date(selectedDateObj);
      endTime.setUTCHours(endHour + 5, endMinute, 0);

      const appointmentDuration =
        new Date(appointment.end).getTime() -
        new Date(appointment.start).getTime();
      const intervalMs = 15 * 60 * 1000;

      while (currentTime < endTime) {
        const slotEndTime = new Date(
          currentTime.getTime() + appointmentDuration
        );

        // Check for conflicts with existing appointments
        const hasConflict = appointmentsOnDate.some((apt) => {
          const aptStart = new Date(apt.start).getTime();
          const aptEnd = new Date(apt.end).getTime();
          const slotStart = currentTime.getTime();
          const slotEnd = slotEndTime.getTime();

          return (
            (slotStart >= aptStart && slotStart < aptEnd) || // Slot starts during an appointment
            (slotEnd > aptStart && slotEnd <= aptEnd) || // Slot ends during an appointment
            (slotStart <= aptStart && slotEnd >= aptEnd) // Slot fully overlaps an appointment
          );
        });

        if (!hasConflict && slotEndTime <= endTime) {
          slots.push({ startTime: new Date(currentTime) });
        }

        currentTime = new Date(currentTime.getTime() + intervalMs);
      }
    });

    setAvailableSlots(slots);
  }, [selectedDate, clinicianData, clinicianAppointments, appointment, selectedClinic]);

  const handleReschedule = async () => {
    if (!newDateTime) {
      setError("Please select a new date and time");
      return;
    }

    const isSlotAvailable = availableSlots.some(
      (slot) =>
        new Date(slot.startTime).toISOString() ===
        new Date(newDateTime).toISOString()
    );

    if (!isSlotAvailable) {
      setError("Selected time slot is not available");
      return;
    }

    try {
      const newStartTime = new Date(newDateTime);
      const appointmentDuration =
        new Date(appointment.end).getTime() -
        new Date(appointment.start).getTime();
      const newEndTime = new Date(newStartTime.getTime() + appointmentDuration);

      // Create a copy of the appointment object and update relevant fields
      const payload = {
        ...appointment,
        start: newStartTime.toISOString(),
        end: newEndTime.toISOString(),
      };

      if (selectedClinician) {
        payload.clinician = selectedClinician._id;
      }
      
      if (selectedClinic) {
        payload.clinic = selectedClinic._id;
      }

      dispatch(rescheduleAppointment(payload));
      setRescheduleStatus("success");
    } catch (err) {
      setRescheduleStatus("error");
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<td key={`empty-${i}`} className="p-1"></td>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day).toISOString().split("T")[0];
      const isAvailable = daysWithSlots.has(date);
      const isSelected = date === selectedDate;

      days.push(
        <td key={day} className="p-1">
          <button
            onClick={() => setSelectedDate(date)}
            disabled={!isAvailable}
            className={`w-full h-12 rounded-sm text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              ${
                isSelected
                  ? "bg-blue-600 text-white shadow-lg"
                  : isAvailable
                  ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 hover:border-green-300"
                  : "bg-gray-50 text-gray-400 cursor-not-allowed"
              }
            `}>
            <span className="block">{day}</span>
            {isAvailable && (
              <span className="block text-xs opacity-75">
                {isSelected ? "Selected" : "Available"}
              </span>
            )}
          </button>
        </td>
      );

      if ((firstDay + day) % 7 === 0 || day === daysInMonth) {
        weeks.push(<tr key={day}>{days}</tr>);
        days = [];
      }
    }

    return weeks;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-sm shadow-lg p-8 text-center">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (rescheduleStatus === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-sm shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Appointment Rescheduled!
            </h2>
            <p className="text-gray-600 mb-6">
              Your appointment has been successfully rescheduled.
            </p>
            {!clientView && (
              <button
                onClick={onClose}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-sm shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h2 className="text-2xl font-semibold text-white flex items-center">
              <CalendarIcon className="h-6 w-6 mr-3" />
              Reschedule Appointment
            </h2>
            <p className="text-blue-100 mt-1">
              Select a new date and time for your appointment
            </p>
          </div>

          <div className="p-8">
            {appointment && (
              <div className="space-y-8">
                {/* Current Appointment Details */}
                <div className="bg-gray-50 rounded-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
                    Current Appointment
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Client */}
                    <div className="flex flex-col items-center bg-white rounded-md shadow-sm p-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mb-2">
                        <UserIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500">Client</p>
                      <p className="text-gray-900 font-semibold text-lg">
                        {appointment.client.firstName} {appointment.client.lastName}
                      </p>
                    </div>
                    
                    {/* Clinic */}
                    <div className="flex flex-col items-center bg-white rounded-md shadow-sm p-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-2">
                        <CalendarIcon className="h-5 w-5 text-green-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500">Clinic</p>
                      {!clientView ? (
                        isEditingClinic ? (
                          <div className="w-full mt-2">
                            <select
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              onChange={(e) => {
                                const clinic = availableClinics.find(c => c._id === e.target.value);
                                if (clinic) handleClinicSelect(clinic);
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {availableClinics.length > 0 ? 'Select a clinic...' : 'No clinics available for this clinician'}
                              </option>
                              {availableClinics.map((clinic) => (
                                <option key={clinic._id} value={clinic._id}>
                                  {clinic.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-gray-900 font-semibold text-lg text-center">
                              {selectedClinic ? selectedClinic.name : appointment.clinic.name}
                            </p>
                            <button
                              onClick={() => setIsEditingClinic(true)}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                              aria-label="Change clinic"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      ) : (
                        <p className="text-gray-900 font-semibold text-lg mt-1 text-center">
                          {appointment.clinic.name}
                        </p>
                      )}
                    </div>
                    
                    {/* Provider */}
                    <div className="flex flex-col items-center bg-white rounded-md shadow-sm p-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mb-2">
                        <UserIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500">Provider</p>
                      {!clientView ? (
                        isEditingClinician ? (
                          <div className="w-full mt-2">
                            <SearchDropdown
                              prefix=":clinician:"
                              displayFields={[
                                "firstName",
                                "lastName",
                                "qualifications",
                              ]}
                              onSelection={handleClinicianSelect}
                              placeholder="Search for a clinician"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-gray-900 font-semibold text-lg">
                              {selectedClinician
                                ? `${selectedClinician.firstName} ${selectedClinician.lastName}`
                                : `${appointment.clinician.firstName} ${appointment.clinician.lastName}`}
                            </p>
                            <button
                              onClick={() => setIsEditingClinician(true)}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                              aria-label="Edit provider"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      ) : (
                        <p className="text-gray-900 font-semibold text-lg mt-1">
                          {appointment.clinician.firstName} {appointment.clinician.lastName}
                        </p>
                      )}
                    </div>
                    {/* Date & Time */}
                    <div className="flex flex-col items-center bg-white rounded-md shadow-sm p-4 lg:col-start-1">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mb-2">
                        <ClockIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500">Current Date & Time</p>
                      <p className="text-gray-900 font-semibold text-lg text-center">
                        {new Date(appointment.start).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <p className="text-gray-600 text-base">
                        {new Date(appointment.start).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    {/* Service */}
                    <div className="flex flex-col items-center bg-white rounded-md shadow-sm p-4 lg:col-start-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mb-2">
                        <CalendarIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500">Service</p>
                      <p className="text-gray-900 font-semibold text-lg text-center">
                        {appointment.service.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Calendar Section */}
                <div className="bg-white border border-gray-200 rounded-sm p-6">
                  <div className="flex justify-between items-center mb-6">
                    <button
                      onClick={() =>
                        setCurrentMonth(
                          new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth() - 1
                          )
                        )
                      }
                      className="p-2 hover:bg-gray-100 rounded-sm transition-colors">
                      <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {currentMonth.toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </h3>
                    <button
                      onClick={() =>
                        setCurrentMonth(
                          new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth() + 1
                          )
                        )
                      }
                      className="p-2 hover:bg-gray-100 rounded-sm transition-colors">
                      <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>

                  <div className="overflow-hidden rounded-sm border border-gray-200">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            "Sun",
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                          ].map((day) => (
                            <th
                              key={day}
                              className="p-3 text-sm font-medium text-gray-700">
                              {day}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {renderCalendar()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="bg-white border border-gray-200 rounded-sm p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
                      Available Time Slots
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      {new Date(selectedDate).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() =>
                            setNewDateTime(slot.startTime.toISOString())
                          }
                          className={`p-3 text-sm font-medium rounded-sm border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                            ${
                              slot.startTime.toISOString() === newDateTime
                                ? "bg-blue-600 text-white border-blue-600 shadow-lg"
                                : "bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:bg-blue-50"
                            }
                          `}>
                          {slot.startTime.toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </button>
                      ))}
                    </div>
                    {availableSlots.length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        No available time slots for this date.
                      </p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    onClick={handleReschedule}
                    disabled={!newDateTime}
                    className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium">
                    {newDateTime
                      ? "Reschedule Appointment"
                      : "Select a time slot"}
                  </button>

                  {!clientView && (
                    <button
                      onClick={onClose}
                      className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
